/**
 * Contratos de vendas (Fase 5). O mesmo schema valida o formulário no frontend e a
 * requisição na API; o banco repete as regras essenciais (preço de tabela, desconto
 * máximo, limite de crédito, reserva, lote vencido, título/recebimento).
 */
import { z } from "zod";
import { decimalSchema, versionSchema } from "./cadastros";
import { reasonSchema, type StockMovement } from "./estoque";
import type { InstallmentStatus, PayableStatus } from "./financeiro";
import { money, subtractMoney, sumMoney, toCents } from "./money";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const optionalUuid = z
  .union([z.uuid(), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const requiredDate = z.iso.date("Informe a data");

/** Maior valor em R$ que o banco guarda (numeric(14,2)). */
const MONEY_MAX = 999_999_999_999;
const moneyOrZero = z
  .union([decimalSchema(2, { max: MONEY_MAX }), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === null || v === undefined ? "0" : v));
const positiveMoney = decimalSchema(2, { max: MONEY_MAX }).refine(
  (v) => Number(v) > 0,
  "Informe um valor maior que zero",
);

// ---------------------------------------------------------------- situação do pedido
export const SALES_STATUSES = [
  "rascunho",
  "aprovado",
  "confirmado",
  "cancelado",
  "estornado",
] as const;
export type SalesStatus = (typeof SALES_STATUSES)[number];
export const SALES_STATUS_LABEL: Record<SalesStatus, string> = {
  rascunho: "Rascunho",
  aprovado: "Aprovado (estoque reservado)",
  confirmado: "Confirmado",
  cancelado: "Cancelado",
  estornado: "Estornado",
};
export type PaymentMode = "titulo" | "a_vista";
export const PAYMENT_MODE_LABEL: Record<PaymentMode, string> = {
  titulo: "A prazo (contas a receber)",
  a_vista: "Recebido à vista",
};

// ---------------------------------------------------------------- pedido: cabeçalho
export const salesOrderCreateSchema = z.object({
  customerId: z.uuid("Selecione o cliente"),
  /**
   * venda da rota: carga aberta do vendedor (define o depósito e o vendedor). Ausente: o
   * vendedor restrito sem depósito escolhido vende pela carga aberta dele; vazio/null: não é
   * venda da rota.
   */
  loadId: z.union([z.uuid(), z.literal(""), z.null()]).optional(),
  /** vazio: o vendedor do cliente (ou, para quem é vendedor, ele mesmo) */
  salespersonId: optionalUuid,
  /** vazio: depósito padrão (fábrica) */
  warehouseId: optionalUuid,
  /** vazio: a forma de pagamento do cadastro do cliente */
  paymentMethodId: optionalUuid,
  orderDate: z
    .union([z.iso.date("Data inválida"), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v ? v : null)),
  notes: optionalText(1000),
});
export type SalesOrderCreateInput = z.input<typeof salesOrderCreateSchema>;

export const salesInstallmentSchema = z.object({
  dueDate: requiredDate,
  amount: positiveMoney,
});

/** Dados do rascunho + parcelas a receber (vazias quando a forma é à vista). */
export const salesOrderUpdateSchema = z.object({
  customerId: z.uuid("Selecione o cliente"),
  loadId: optionalUuid,
  salespersonId: optionalUuid,
  warehouseId: z.uuid("Selecione o depósito"),
  paymentMethodId: optionalUuid,
  orderDate: requiredDate,
  notes: optionalText(1000),
  installments: z.array(salesInstallmentSchema).max(120, "Máximo de 120 parcelas"),
  ...versionSchema.shape,
});
export type SalesOrderUpdateInput = z.input<typeof salesOrderUpdateSchema>;

// ---------------------------------------------------------------- pedido: itens
export const salesItemSchema = z.object({
  productId: z.uuid("Selecione o produto"),
  unitId: z.uuid("Selecione a unidade"),
  quantity: decimalSchema(6).refine(
    (v) => Number(v) > 0,
    "A quantidade precisa ser maior que zero",
  ),
  /** preço praticado (vazio = preço da tabela) */
  unitPrice: z
    .union([decimalSchema(4, { max: 9_999_999_999 }), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v)),
  discount: moneyOrZero,
  notes: optionalText(300),
});
export type SalesItemInput = z.input<typeof salesItemSchema>;

export const salesItemsSchema = z.object({
  items: z.array(salesItemSchema).max(500, "Máximo de 500 itens"),
  /** frete e outras despesas cobrados do cliente: rateados pelo valor dos itens */
  freight: moneyOrZero,
  other: moneyOrZero,
  ...versionSchema.shape,
});
export type SalesItemsInput = z.input<typeof salesItemsSchema>;

/**
 * Salvamento único do pedido (v6, D13): dados, parcelas, itens, frete e outras despesas numa
 * só transação, com UMA trava de versão.
 */
export const salesOrderDraftSchema = salesOrderUpdateSchema.extend({
  items: z.array(salesItemSchema).max(500, "Máximo de 500 itens"),
  freight: moneyOrZero,
  other: moneyOrZero,
});
export type SalesOrderDraftInput = z.input<typeof salesOrderDraftSchema>;

/** Novo pedido já com itens e parcelas (opcionais: sem eles, como antes). */
export const salesOrderNewSchema = salesOrderCreateSchema.extend({
  items: z.array(salesItemSchema).max(500, "Máximo de 500 itens").optional(),
  freight: moneyOrZero,
  other: moneyOrZero,
  installments: z.array(salesInstallmentSchema).max(120, "Máximo de 120 parcelas").optional(),
});
export type SalesOrderNewInput = z.input<typeof salesOrderNewSchema>;

/**
 * Prévia do resumo do pedido enquanto os itens não foram salvos, com a MESMA fórmula do banco
 * (total = produtos − descontos + frete + outras; produtos = soma de quantidade × preço,
 * arredondada por linha). Valores já normalizados (texto com ponto). Linha sem preço ainda
 * conhecido deixa produtos e total em aberto (null).
 */
export function salesOrderTotalsPreview(input: {
  lineValues: (string | null)[];
  discounts: string[];
  freight: string;
  other: string;
}): {
  products: string | null;
  discounts: string;
  freight: string;
  other: string;
  total: string | null;
} {
  const num = (v: string) => (/^\d+(\.\d+)?$/.test(v) ? v : "0");
  const discounts = sumMoney(input.discounts.map(num));
  const freight = money(num(input.freight));
  const other = money(num(input.other));
  const complete = input.lineValues.every((v): v is string => v !== null);
  const products = complete ? sumMoney(input.lineValues as string[]) : null;
  const total =
    products === null ? null : sumMoney([subtractMoney(products, discounts), freight, other]);
  return { products, discounts, freight, other, total };
}

// ---------------------------------------------------------------- liberação comercial
/**
 * Por que o pedido exigiu a liberação comercial. A regra (banco, 0008) é UMA só para os dois
 * casos — item fora da tabela de preço OU desconto acima do máximo da tabela —, com a mesma
 * permissão (vendas.desconto.acima_limite); aqui só se diz qual dos dois aconteceu.
 */
export type CommercialReleaseReason = "preco_fora_da_tabela" | "desconto_acima_do_maximo";

type ReleaseItem = {
  productCode: string;
  offList: boolean;
  overLimit: boolean;
  discountPercent: number | null;
  maxDiscountPercent: string | null;
};

export function commercialReleaseReasons(items: readonly ReleaseItem[]): CommercialReleaseReason[] {
  const out: CommercialReleaseReason[] = [];
  if (items.some((i) => i.offList)) out.push("preco_fora_da_tabela");
  if (items.some((i) => i.overLimit && !i.offList)) out.push("desconto_acima_do_maximo");
  return out;
}

const pctText = (v: number | string | null) =>
  v === null ? "—" : `${String(Number(Number(v).toFixed(2))).replace(".", ",")}%`;

/** "preço fora da tabela (CAFE500)", "desconto acima do máximo (CAFE250: 12% de 10%)". */
export function commercialReleaseDetails(items: readonly ReleaseItem[]): string[] {
  const off = items.filter((i) => i.offList).map((i) => i.productCode);
  const over = items
    .filter((i) => i.overLimit && !i.offList)
    .map(
      (i) => `${i.productCode}: ${pctText(i.discountPercent)} de ${pctText(i.maxDiscountPercent)}`,
    );
  const out: string[] = [];
  if (off.length) out.push(`preço fora da tabela (${off.join(", ")})`);
  if (over.length) out.push(`desconto acima do máximo (${over.join("; ")})`);
  return out;
}

// ---------------------------------------------------------------- ações
/** Aprovar / confirmar: liberações explícitas de quem tem a permissão. */
export const salesApprovalSchema = z.object({
  ...versionSchema.shape,
  /** desconto acima do máximo da tabela (exige vendas.desconto.acima_limite) */
  releaseDiscount: z.boolean().default(false),
  /** venda acima do limite de crédito (exige vendas.credito.liberar) */
  releaseCredit: z.boolean().default(false),
});
export type SalesApprovalInput = z.input<typeof salesApprovalSchema>;

export const salesCancelSchema = z.object({
  ...versionSchema.shape,
  reason: reasonSchema,
});
export const salesReverseSchema = z.object({ reason: reasonSchema });

export const salesQuerySchema = z.object({
  status: z.enum([...SALES_STATUSES, "todos"]).default("todos"),
  q: z.string().trim().max(100).optional(),
  customerId: z.uuid().optional(),
  salespersonId: z.uuid().optional(),
  loadId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const priceQuerySchema = z.object({
  customerId: z.uuid(),
  productId: z.uuid(),
  unitId: z.uuid(),
  warehouseId: z.uuid().optional(),
});

// ---------------------------------------------------------------- regras compartilhadas
/**
 * Desconto acima do máximo da tabela? Compara o valor líquido do item com
 * valor de tabela × (1 − desconto máximo), em centavos (mesma conta do banco).
 */
export function isDiscountOverLimit(item: {
  listValue: string | null;
  maxDiscountPercent: string | null;
  productsValue: string;
  discount: string;
}): boolean {
  if (item.listValue === null || item.maxDiscountPercent === null) return false;
  // em centavos e pontos-base (inteiros), arredondando meio para cima como o banco
  const bp = BigInt(Math.round(Number(item.maxDiscountPercent) * 100));
  const min = (toCents(item.listValue) * (10000n - bp) + 5000n) / 10000n;
  return toCents(item.productsValue) - toCents(item.discount) < min;
}

/** Desconto efetivo em % sobre o valor de tabela (null = sem preço de tabela). */
export function effectiveDiscountPercent(item: {
  listValue: string | null;
  productsValue: string;
  discount: string;
}): number | null {
  const list = item.listValue === null ? 0 : Number(item.listValue);
  if (list <= 0) return null;
  const net = Number(item.productsValue) - Number(item.discount);
  return Math.round(((list - net) / list) * 10000) / 100;
}

// ---------------------------------------------------------------- respostas
export type SalesOrderItem = {
  id: string;
  lineNumber: number;
  productId: string;
  productCode: string;
  productName: string;
  controlsLot: boolean;
  unitId: string;
  unitCode: string;
  baseUnitCode: string;
  quantity: string;
  quantityBase: string;
  listPrice: string | null;
  maxDiscountPercent: string | null;
  unitPrice: string;
  productsValue: string;
  listValue: string | null;
  discount: string;
  freight: string;
  other: string;
  total: string;
  /** desconto efetivo sobre a tabela, em % */
  discountPercent: number | null;
  overLimit: boolean;
  /** fora da tabela de preço (preço livre): exige a mesma liberação do desconto */
  offList: boolean;
  notes: string | null;
};

export type SalesInstallment = { id: string; number: number; dueDate: string; amount: string };

export type SalesReceipt = {
  id: string;
  paymentMethodId: string;
  paymentMethodName: string;
  paymentKind: string;
  nfePaymentCode: string;
  amount: string;
  receivedAt: string;
  receivedByEmail: string | null;
  status: "recebido" | "estornado";
  reversedAt: string | null;
};

/** Disponibilidade de um produto num depósito. */
export type Availability = {
  warehouseId: string;
  productId: string;
  /** saldo total (inclui lotes vencidos) */
  balance: string;
  /** saldo em lotes dentro da validade (ou sem lote) */
  sellable: string;
  reserved: string;
  /** v7: vendas sem estoque ainda a regularizar (compromisso, como a reserva) */
  pending: string;
  /** vendável − reservado − pendências */
  available: string;
};

/** Linha da consulta de disponibilidade (Estoque → Saldos). */
export type AvailabilityRow = Availability & {
  warehouseName: string;
  productCode: string;
  productName: string;
  baseUnitCode: string;
  /** saldo em lotes vencidos (não vendável) */
  expired: string;
  /** v7: saldo − pendências de venda sem estoque (negativo quando falta regularizar) */
  projected: string;
};

export const availabilityQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  warehouseId: z.uuid().optional(),
  /** só produtos com reserva */
  reservados: z
    .enum(["1", "0"])
    .default("0")
    .transform((v) => v === "1"),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type SalesOrder = {
  id: string;
  number: string;
  status: SalesStatus;
  customerId: string;
  customerName: string;
  customerDocument: string | null;
  salespersonId: string | null;
  salespersonName: string | null;
  warehouseId: string;
  warehouseName: string;
  /** venda da rota (Fase 6) */
  loadId: string | null;
  loadNumber: string | null;
  /** v7: pedido comum ou gerado pela Venda Direta */
  origin: SalesOrigin;
  /** v7: aprovação que admitiu venda sem estoque (quem e quando) */
  stockShortageByEmail: string | null;
  stockShortageAt: string | null;
  priceListId: string | null;
  priceListName: string | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  /** título (a prazo) ou à vista — definido pela forma de pagamento */
  paymentMode: PaymentMode | null;
  /** o que a forma de pagamento escolhida vai gerar (antes de aprovar) */
  expectedPaymentMode: PaymentMode | null;
  orderDate: string;
  productsTotal: string;
  discountTotal: string;
  freightTotal: string;
  otherTotal: string;
  totalAmount: string;
  commissionPercent: string | null;
  discountOverrideByEmail: string | null;
  discountOverrideAt: string | null;
  creditReleasedByEmail: string | null;
  creditReleasedAt: string | null;
  creditLimitSnapshot: string | null;
  creditExposureSnapshot: string | null;
  notes: string | null;
  itemCount: number;
  createdAt: string;
  createdByEmail: string | null;
  approvedAt: string | null;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  version: number;
  receivableId: string | null;
  receivableNumber: string | null;
  receivableStatus: PayableStatus | null;
  items?: SalesOrderItem[];
  installments?: SalesInstallment[];
  receipt?: SalesReceipt | null;
  movements?: StockMovement[];
  /** crédito do cliente (limite, em aberto sem este pedido) — só para a prazo */
  credit?: { limit: string | null; exposure: string; exceeded: boolean } | null;
  /** disponibilidade dos produtos do pedido no depósito */
  availability?: (Availability & {
    productCode: string;
    needed: string;
    enough: boolean;
    /** v7: por que a venda sem estoque não vale para este produto (rota, rastreabilidade) */
    blockedReason?: string;
  })[];
  /** o que falta para aprovar/confirmar (vazio = pronto) */
  issues?: string[];
  /** avisos e liberações necessárias (não impedem por si) */
  warnings?: string[];
  /** liberações que a aprovação vai exigir */
  needsDiscountRelease?: boolean;
  /** falta estoque disponível (rascunho): a aprovação será recusada */
  stockShort?: boolean;
  needsCreditRelease?: boolean;
  /**
   * v7: o que vai ficar como venda sem estoque, por produto (quem tem a permissão; antes de
   * confirmar). Vazio quando há estoque para tudo.
   */
  projectedShortages?: {
    productId: string;
    productCode: string;
    needed: string;
    available: string;
    short: string;
  }[];
  /** v7: faltas registradas na confirmação (venda sem estoque) */
  shortages?: {
    itemId: string;
    productCode: string;
    quantity: string;
    regularized: string;
    status: StockShortageStatus;
  }[];
};

/** v7: origem do pedido */
export const SALES_ORIGINS = ["pedido", "venda_direta"] as const;
export type SalesOrigin = (typeof SALES_ORIGINS)[number];
export const SALES_ORIGIN_LABEL: Record<SalesOrigin, string> = {
  pedido: "Pedido",
  venda_direta: "Venda Direta",
};

/** v7: situação de uma pendência de venda sem estoque */
export const STOCK_SHORTAGE_STATUSES = ["aberta", "regularizada", "cancelada"] as const;
export type StockShortageStatus = (typeof STOCK_SHORTAGE_STATUSES)[number];
export const STOCK_SHORTAGE_STATUS_LABEL: Record<StockShortageStatus, string> = {
  aberta: "A regularizar",
  regularizada: "Regularizada",
  cancelada: "Cancelada (venda estornada)",
};

export type PriceLookup = {
  priceListId: string | null;
  priceListName: string | null;
  listPrice: string | null;
  maxDiscountPercent: string | null;
  availability: Availability | null;
};

// ---------------------------------------------------------------- contas a receber
export type ReceivableInstallmentRow = {
  id: string;
  receivableId: string;
  receivableNumber: string;
  customerId: string;
  customerName: string;
  description: string;
  salesOrderId: string | null;
  salesOrderNumber: string | null;
  number: number;
  installmentCount: number;
  dueDate: string;
  amount: string;
  paidAmount: string;
  openAmount: string;
  status: InstallmentStatus;
  overdue: boolean;
  daysToDue: number;
  version: number;
};

export type ReceivableSummary = {
  openTotal: string;
  overdueTotal: string;
  next7DaysTotal: string;
  openCount: number;
  overdueCount: number;
};

export type ReceivableList = {
  items: ReceivableInstallmentRow[];
  total: number;
  limit: number;
  offset: number;
  filteredOpenTotal: string;
  summary: ReceivableSummary;
};

export type Receivable = {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  customerDocument: string | null;
  salesOrderId: string | null;
  salesOrderNumber: string | null;
  description: string;
  issueDate: string;
  totalAmount: string;
  status: PayableStatus;
  cancelledAt: string | null;
  cancelReason: string | null;
  notes: string | null;
  createdAt: string;
  createdByEmail: string | null;
  version: number;
  installments: {
    id: string;
    number: number;
    dueDate: string;
    amount: string;
    paidAmount: string;
    status: InstallmentStatus;
    overdue: boolean;
    version: number;
  }[];
};

export const receivableQuerySchema = z.object({
  situacao: z
    .enum(["abertas", "vencidas", "a_vencer", "pagas", "canceladas", "todas"])
    .default("abertas"),
  customerId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
